// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 46 — Missing Track Finder / Playlist Gap Analysis
// ============================================================================

/** The kind of external source a playlist gap analysis is run against. */
export type DJSEMGapAnalysisSourceType =
  | "spotify_playlist" | "soundcloud_playlist" | "rekordbox_playlist"
  | "djsem_playlist" | "djsem_set" | "event_brief_must_play" | "manual_track_list";

/** How well a source track reference is matched to the local library. */
export type DJSEMTrackAvailabilityStatusValue =
  | "available_exact" | "available_probable" | "weak_match" | "missing"
  | "broken_local_file" | "duplicate_candidates" | "excluded_do_not_play" | "unknown";

/** The library-availability match result for a single source track reference. */
export interface DJSEMTrackAvailabilityStatus {
  sourceTrackRefId: string;
  title?: string; artist?: string;
  status: DJSEMTrackAvailabilityStatusValue;
  matchedTrackIds: string[];
  confidence: number;
  reasons: string[];
  warnings: string[];
}

/** The full result of comparing a source track list against the local library. */
export interface DJSEMPlaylistGapAnalysisReport {
  id: string;
  sourceType: DJSEMGapAnalysisSourceType;
  sourceId: string;
  totalTracks: number;
  exactAvailableCount: number;
  probableAvailableCount: number;
  weakMatchCount: number;
  missingCount: number;
  brokenFileCount: number;
  readinessScore: number;
  availability: DJSEMTrackAvailabilityStatus[];
  wishlistId?: string;
  warnings: string[];
  createdAt: string;
}

/** A single track needed to close a gap identified by playlist gap analysis. */
export interface DJSEMAcquisitionWishlistItem {
  id: string;
  title?: string; artist?: string;
  desiredUse: string;
  status: "needed" | "found" | "not_available" | "ignored";
  matchedTrackId?: string;
  warnings: string[];
}

/** A tracked list of missing/weak-match tracks to acquire, derived from a gap analysis report. */
export interface DJSEMAcquisitionWishlist {
  id: string;
  projectId: string;
  title: string;
  items: DJSEMAcquisitionWishlistItem[];
  status: "draft" | "in_progress" | "resolved" | "archived";
  warnings: string[];
  createdAt: string;
}

/** A single track reference from an external/source playlist being checked for library availability. */
export interface GapAnalysisTrackRef {
  id: string; title?: string; artist?: string; durationMs?: number; hash?: string; fileRef?: string;
}

/** A local library track candidate used to resolve gap-analysis matches. */
export interface GapAnalysisLibraryTrack {
  id: string; title?: string; artist?: string; durationMs?: number; hash?: string; fileRef?: string;
}

/** Compares a source playlist's tracks against the local library and manages the resulting acquisition wishlist. */
export class PlaylistGapAnalysisService {
  /**
   * Matches each source track reference to the library (by exact hash, then title+artist+duration,
   * then title+artist, falling back to missing/broken-file), and scores overall readiness.
   * @param sourceType - the kind of external source being analysed.
   * @param sourceId - id of the source playlist/set/list.
   * @param sourceRefs - the source's track references to resolve.
   * @param library - the local library to match against.
   * @returns the gap analysis report with per-track availability and an overall readiness score.
   */
  analyse(sourceType: DJSEMGapAnalysisSourceType, sourceId: string, sourceRefs: GapAnalysisTrackRef[], library: GapAnalysisLibraryTrack[]): DJSEMPlaylistGapAnalysisReport {
    const availability: DJSEMTrackAvailabilityStatus[] = [];
    for (const ref of sourceRefs) {
      // Priority: exact hash → title+artist+duration → title+artist → missing
      const exactHash = ref.hash ? library.find(l => l.hash === ref.hash) : undefined;
      if (exactHash) { availability.push({ sourceTrackRefId: ref.id, title: ref.title, artist: ref.artist, status: "available_exact", matchedTrackIds: [exactHash.id], confidence: 1, reasons: ["exact hash match"], warnings: [] }); continue; }
      const titleArtistDur = library.filter(l => l.title && ref.title && l.title.toLowerCase() === ref.title.toLowerCase() && l.artist && ref.artist && l.artist.toLowerCase() === ref.artist.toLowerCase() && l.durationMs && ref.durationMs && Math.abs(l.durationMs - ref.durationMs) < 3000);
      if (titleArtistDur.length > 0) { availability.push({ sourceTrackRefId: ref.id, title: ref.title, artist: ref.artist, status: "available_probable", matchedTrackIds: titleArtistDur.map(t => t.id), confidence: 0.8, reasons: ["title+artist+duration match"], warnings: [] }); continue; }
      const titleArtist = library.filter(l => l.title && ref.title && l.title.toLowerCase() === ref.title.toLowerCase() && l.artist && ref.artist && l.artist.toLowerCase() === ref.artist.toLowerCase());
      if (titleArtist.length > 0) { availability.push({ sourceTrackRefId: ref.id, title: ref.title, artist: ref.artist, status: "weak_match", matchedTrackIds: titleArtist.map(t => t.id), confidence: 0.5, reasons: ["title+artist match, duration unknown"], warnings: ["Weak match — verify manually"] }); continue; }
      // Check broken file ref
      if (ref.fileRef) {
        const broken = library.find(l => l.id === ref.id && !l.fileRef);
        if (broken) { availability.push({ sourceTrackRefId: ref.id, title: ref.title, artist: ref.artist, status: "broken_local_file", matchedTrackIds: [broken.id], confidence: 0.9, reasons: ["file ref missing"], warnings: [] }); continue; }
      }
      availability.push({ sourceTrackRefId: ref.id, title: ref.title, artist: ref.artist, status: "missing", matchedTrackIds: [], confidence: 1, reasons: ["no library match found"], warnings: [] });
    }
    const exact = availability.filter(a => a.status === "available_exact").length;
    const probable = availability.filter(a => a.status === "available_probable").length;
    const weak = availability.filter(a => a.status === "weak_match").length;
    const missing = availability.filter(a => a.status === "missing").length;
    const broken = availability.filter(a => a.status === "broken_local_file").length;
    const readiness = sourceRefs.length > 0 ? (exact + probable * 0.8) / sourceRefs.length : 0;
    return { id: `gar_${_seq.next()}`, sourceType, sourceId, totalTracks: sourceRefs.length, exactAvailableCount: exact, probableAvailableCount: probable, weakMatchCount: weak, missingCount: missing, brokenFileCount: broken, readinessScore: readiness, availability, warnings: [], createdAt: AT };
  }

  /**
   * Builds an acquisition wishlist from a gap analysis report's missing and weak-match tracks.
   * @param projectId - the project the wishlist belongs to.
   * @param report - the gap analysis report to derive wishlist items from.
   * @returns a new draft wishlist.
   */
  createWishlist(projectId: string, report: DJSEMPlaylistGapAnalysisReport): DJSEMAcquisitionWishlist {
    const items = report.availability.filter(a => a.status === "missing" || a.status === "weak_match").map(a => ({
      id: `wi_${_seq.next()}`, title: a.title, artist: a.artist, desiredUse: "set", status: "needed" as const, warnings: a.warnings,
    }));
    return { id: `wl_${_seq.next()}`, projectId, title: `Wishlist from ${report.sourceType}`, items, status: "draft", warnings: [], createdAt: AT };
  }

  /**
   * Marks a wishlist item as found and links it to the resolving library track.
   * @param wishlist - the wishlist to update.
   * @param itemId - id of the wishlist item being resolved.
   * @param matchedTrackId - id of the library track that resolves the item.
   * @returns a copy of the wishlist with the matching item updated to `"found"`.
   */
  resolveWishlistItem(wishlist: DJSEMAcquisitionWishlist, itemId: string, matchedTrackId: string): DJSEMAcquisitionWishlist {
    return { ...wishlist, items: wishlist.items.map(i => i.id === itemId ? { ...i, status: "found" as const, matchedTrackId } : i) };
  }
}
